import { useState, useCallback, useEffect, useRef } from 'react';
import { ChatMessage } from '../hooks/useChat';
import { getEditableMessageBody } from '../../../utils/messageSignature';
import { getMessageCaption, isMessageEditable } from '../components/chat-window/useMessageCaption';

const EMPTY_MESSAGE_ERROR = 'Digite uma mensagem antes de enviar.';

/** Clique direito (desktop) ou toque longo (PWA) — só o que o menu precisa. */
export type MessageMenuTrigger = {
    preventDefault: () => void;
    currentTarget: EventTarget | null;
    clientY: number;
};

interface UseChatUIInteractionsProps {
    onSendMessage: (text: string, quotedMessage?: { id: string; sender: string; text?: string }) => boolean | void;
    onEditMessage?: (messageId: string, newText: string, originalText?: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    conversationId?: string;
}

export function useChatUIInteractions({ onSendMessage, onEditMessage, onDeleteMessage, conversationId }: UseChatUIInteractionsProps) {
    const [messageText, setMessageText] = useState('');
    const [sendValidationError, setSendValidationError] = useState<string | null>(null);
    const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
    const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; message: ChatMessage } | null>(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

    const messageTextRef = useRef(messageText);
    const editingMessageRef = useRef(editingMessage);
    const replyingToRef = useRef(replyingTo);
    messageTextRef.current = messageText;
    editingMessageRef.current = editingMessage;
    replyingToRef.current = replyingTo;

    useEffect(() => {
        setReplyingTo(null);
        setEditingMessage(null);
        setContextMenu(null);
        setIsSearchOpen(false);
        setHighlightedMessageId(null);
        setMessageText('');
        setSendValidationError(null);
    }, [conversationId]);

    const updateMessageText = useCallback((text: string) => {
        setMessageText(text);
        if (sendValidationError) {
            setSendValidationError(null);
        }
    }, [sendValidationError]);

    const handleSend = useCallback(() => {
        const trimmed = messageTextRef.current.trim();
        if (!trimmed) {
            setSendValidationError(EMPTY_MESSAGE_ERROR);
            return;
        }

        const currentEdit = editingMessageRef.current;
        const currentReply = replyingToRef.current;

        const quotedPayload = currentReply ? {
            id: currentReply.id,
            sender: currentReply.sender,
            text: currentReply.text
        } : undefined;
        if (currentEdit && onEditMessage) {
            onEditMessage(currentEdit.id, trimmed, currentEdit.text);
        } else if (onSendMessage(trimmed, quotedPayload) === false) {
            return;
        }

        messageTextRef.current = '';
        setSendValidationError(null);
        setMessageText('');
        setEditingMessage(null);
        setReplyingTo(null);
    }, [onSendMessage, onEditMessage]);

    const handleEdit = useCallback((message: ChatMessage) => {
        if (!isMessageEditable(message)) return;

        const raw = message.type === 'text' ? message.text : getMessageCaption(message, true, false);
        const editableText = getEditableMessageBody(raw);
        if (!editableText) return;

        setEditingMessage(message);
        setMessageText(editableText);
        setContextMenu(null);
        setReplyingTo(null);
    }, []);

    const handleDelete = useCallback((message: ChatMessage) => {
        if (message.sender === 'me' && onDeleteMessage) {
            onDeleteMessage(message.id);
            setContextMenu(null);
        }
    }, [onDeleteMessage]);

    const handleContextMenu = useCallback((e: MessageMenuTrigger, message: ChatMessage) => {
        e.preventDefault();
        const bubble = e.currentTarget;
        if (!(bubble instanceof HTMLElement)) return;
        const rect = bubble.getBoundingClientRect();
        const x = message.sender === 'me' ? rect.right : rect.left;
        const y = Math.min(Math.max(e.clientY, rect.top), rect.bottom);
        setContextMenu({ x, y, message });
    }, []);

    const handleReply = useCallback((message: ChatMessage) => {
        setReplyingTo(message);
        setContextMenu(null);
    }, []);

    const handleToggleSearch = useCallback(() => {
        setIsSearchOpen(prev => !prev);
    }, []);

    const handleCloseSearch = useCallback(() => {
        setIsSearchOpen(false);
        setHighlightedMessageId(null);
    }, []);

    const handleHighlightChange = useCallback((messageId: string | null) => {
        setHighlightedMessageId(messageId);
    }, []);

    return {
        messageText,
        setMessageText: updateMessageText,
        sendValidationError,
        replyingTo,
        setReplyingTo,
        editingMessage,
        setEditingMessage,
        contextMenu,
        setContextMenu,
        isSearchOpen,
        highlightedMessageId,
        handleSend,
        handleEdit,
        handleDelete,
        handleContextMenu,
        handleReply,
        handleToggleSearch,
        handleCloseSearch,
        handleHighlightChange
    };
}
